import { subscriptionQueryKeys } from '@/lib/subscription-query-keys';
import { useQuery } from '@tanstack/react-query';
import { accountService, DelinquentCharge, UserSubscription } from '@/services/account.service';
import { useAuth } from '@/contexts/auth.context';
import type { TerminalSubscription } from '@/services/user.service';

export interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  hasOverdueInvoice: boolean;
  daysOverdue: number;
  isBlocked: boolean;
  blockReason: 'no_subscription' | 'payment_overdue' | 'trial_expired' | 'subscription_cancelled' | null;
  subscription: UserSubscription | null;
  overdueInvoice: DelinquentCharge | null;
  terminalSubscription: TerminalSubscription | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Consulta única da assinatura do usuário. Todos os leitores (guards, banners,
 * Minha Conta) usam estas opções para enxergar a mesma cópia em cache — é ela
 * que a reconciliação de pagamento atualiza quando a VIO confirma uma troca.
 */
export const mySubscriptionQueryOptions = {
  queryKey: subscriptionQueryKeys.subscription,
  queryFn: () => accountService.getMySubscription(),
  staleTime: 1000 * 60 * 5, // 5 minutes
  retry: 1,
};

export function useSubscriptionStatus(): SubscriptionStatus {
  const { user } = useAuth();
  const terminalSubscription = user?.terminal_subscription ?? null;

  const {
    data: subscription,
    isLoading,
    error,
  } = useQuery({ ...mySubscriptionQueryOptions, enabled: !!user });

  // Check if has active subscription
  const hasActiveSubscription = !!(
    subscription &&
    subscription.is_active &&
    ['active', 'trial'].includes(subscription.status?.toLowerCase() || '')
  );

  const avaliacao =
    subscription !== undefined
      ? avaliarBloqueio({ subscription, terminalSubscription })
      : SEM_BLOQUEIO;

  return {
    hasActiveSubscription,
    hasOverdueInvoice: avaliacao.hasOverdueInvoice,
    daysOverdue: avaliacao.daysOverdue,
    isBlocked: avaliacao.isBlocked,
    blockReason: avaliacao.blockReason,
    subscription: subscription ?? null,
    overdueInvoice: avaliacao.overdueInvoice,
    terminalSubscription,
    isLoading,
    error: error as Error | null,
  };
}

export interface AvaliacaoBloqueio {
  isBlocked: boolean;
  blockReason: 'no_subscription' | 'payment_overdue' | 'trial_expired' | 'subscription_cancelled' | null;
  hasOverdueInvoice: boolean;
  daysOverdue: number;
  overdueInvoice: DelinquentCharge | null;
}

const SEM_BLOQUEIO: AvaliacaoBloqueio = {
  isBlocked: false,
  blockReason: null,
  hasOverdueInvoice: false,
  daysOverdue: 0,
  overdueInvoice: null,
};

/**
 * Decide qual tela mostrar. A regra de inadimplência NÃO é refeita aqui: o
 * backend a avalia só com as cobranças da assinatura vigente (cobranças órfãs
 * de assinaturas anteriores nunca bloqueiam — TK-1623) e a serve em
 * `subscription.payment_delinquency`. Função pura, para ser testável.
 */
export function avaliarBloqueio({
  subscription,
  terminalSubscription,
}: {
  subscription: UserSubscription | null | undefined;
  terminalSubscription?: TerminalSubscription | null;
}): AvaliacaoBloqueio {
  // Assinatura encerrada (cancelada/inativa). Vem ANTES da inadimplência de
  // propósito: a fatura em aberto do plano morto não pode levar o assinante à
  // tela de "Pagar Fatura", porque pagá-la não reativa nada — a assinatura
  // correspondente na VIO Pay também está cancelada.
  if (!subscription && terminalSubscription) {
    return { ...SEM_BLOQUEIO, isBlocked: true, blockReason: 'subscription_cancelled' };
  }

  if (!subscription) {
    // Nenhuma assinatura existente → precisa escolher um plano.
    return { ...SEM_BLOQUEIO, isBlocked: true, blockReason: 'no_subscription' };
  }

  // Plano gratuito nunca cai em tela de bloqueio: a saída para o gratuito é a
  // garantia de que o assinante sempre volta a usar o sistema.
  if (subscription.is_free_plan) {
    return SEM_BLOQUEIO;
  }

  // Bloqueio por inadimplência (assinatura SUSPENDED ou cobrança acima do
  // limite): o usuário vê "Pagamento Pendente / Pagar Fatura" e mantém o
  // caminho de regularização, em vez de "Assinatura Necessária".
  const delinquency = subscription.payment_delinquency;
  if (delinquency?.is_blocked) {
    const charge = delinquency.blocking_charge;
    return {
      isBlocked: true,
      blockReason: 'payment_overdue',
      hasOverdueInvoice: charge !== null,
      daysOverdue: charge?.days_overdue ?? 0,
      overdueInvoice: charge,
    };
  }

  // Assinatura presente, mas não ativa e sem inadimplência: fallback
  // conservador para o fluxo de seleção de plano.
  // Trial expirado NÃO bloqueia: o backend migra automaticamente para o plano
  // gratuito no primeiro acesso (GET /billing/my-subscription) e o front exibe
  // um aviso não-bloqueante (TrialEndedModal) via `trial_just_ended`.
  const hasActiveSubscription =
    !!subscription.is_active &&
    ['active', 'trial'].includes(subscription.status?.toLowerCase() || '');
  if (!hasActiveSubscription) {
    return { ...SEM_BLOQUEIO, isBlocked: true, blockReason: 'no_subscription' };
  }

  return SEM_BLOQUEIO;
}
